import api from '@/api/axiosConfig.js';
import { executeReceiptOperation } from '@/utils/receipt-operation.js';

export async function fetchMinutaActiva({ sucursalId, cliente, fechaDesde, fechaHasta, soloMinuta = false } = {}) {
  const params = new URLSearchParams();
  if (sucursalId) params.set('sucursal_id', sucursalId);
  if (cliente) params.set('cliente', cliente);
  if (fechaDesde) params.set('fecha_desde', fechaDesde);
  if (fechaHasta) params.set('fecha_hasta', fechaHasta);
  if (soloMinuta) params.set('solo_minuta', '1');
  const query = params.toString();
  const response = await api.get(`/api/cobranzas/minuta/activa${query ? `?${query}` : ''}`);
  return response.data?.data;
}

export async function fetchMinuta({ id, cliente, fechaDesde, fechaHasta } = {}) {
  const params = new URLSearchParams();
  if (cliente) params.set('cliente', cliente);
  if (fechaDesde) params.set('fecha_desde', fechaDesde);
  if (fechaHasta) params.set('fecha_hasta', fechaHasta);
  const query = params.toString();
  const response = await api.get(`/api/cobranzas/minuta/${id}${query ? `?${query}` : ''}`);
  return response.data?.data;
}

export async function abrirMinuta(payload) {
  const response = await api.post('/api/cobranzas/minuta/abrir', payload);
  return response.data?.data;
}

export async function registrarCobro(payload) {
  return executeReceiptOperation('cobrar', payload, async (body, key, assertScope) => {
    const response = await api.post('/api/cobranzas/cobrar', body, { headers: { 'X-Idempotency-Key': key }, receiptOperationGuard: assertScope });
    if (!(response.data?.data?.cobranzaId || response.data?.data?.reciboId || response.data?.data?.numeroRecibo)) throw new Error('No se recibió confirmación de la cobranza. Reintente la operación original.');
    return response.data.data;
  });
}

export async function fetchDocumentosCliente({ clienteId, sucursalId } = {}) {
  const params = new URLSearchParams();
  if (clienteId) params.set('cliente_id', clienteId);
  if (sucursalId) params.set('sucursal_id', sucursalId);
  const query = params.toString();
  const response = await api.get(`/api/cobranzas/documentos${query ? `?${query}` : ''}`);
  return response.data?.data;
}

export async function fetchBancosActivos() {
  const response = await api.get('/api/cobranzas/bancos-activos');
  return response.data?.data;
}

export async function registrarCobranza(payload, { operationContext = payload?.contexto || 'GENERAL' } = {}) {
  return executeReceiptOperation(`registrar:${operationContext}`, payload, async (body, key, assertScope) => {
    const response = await api.post('/api/cobranzas/registrar', body, { headers: { 'X-Idempotency-Key': key }, receiptOperationGuard: assertScope });
    if (!(response.data?.data?.cobranzaId || response.data?.data?.reciboId || response.data?.data?.numeroRecibo)) throw new Error('No se recibió confirmación de la cobranza. Reintente la operación original.');
    return response.data.data;
  });
}

export async function fetchValoresPendientesRendicion(filters = {}) {
  const response = await api.get('/api/cobranzas/rendiciones/pendientes', { params: filters });
  return response.data?.data;
}

export async function fetchRendiciones(filters = {}) {
  const response = await api.get('/api/cobranzas/rendiciones', { params: filters });
  return response.data?.data;
}

export async function fetchRendicion(id) {
  const response = await api.get(`/api/cobranzas/rendiciones/${id}`);
  return response.data?.data;
}

export async function presentarRendicion(payload, idempotencyKey) {
  const response = await api.post('/api/cobranzas/rendiciones', payload, {
    headers: idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : undefined,
  });
  return response.data?.data;
}

export async function recibirRendicion(id, payload) {
  const response = await api.post(`/api/cobranzas/rendiciones/${id}/recibir`, payload);
  return response.data?.data;
}

export async function conciliarTransferenciaCobranza(id, referenciaBancaria) {
  const response = await api.post(`/api/cobranzas/rendiciones/pagos/${id}/conciliar`, { referencia_bancaria: referenciaBancaria });
  return response.data?.data;
}

export async function aplicarCreditoCuenta(aplicacionId, { facturaId, importe, cobranzaId }) {
  const byCollection = aplicacionId == null || String(cobranzaId).startsWith('GQL-');
  if (byCollection && !cobranzaId) throw new Error('No se pudo identificar la cobranza del crédito.');
  const endpoint = byCollection
    ? `/api/cobranzas/${encodeURIComponent(cobranzaId)}/credito/aplicar`
    : `/api/cobranzas/creditos/${encodeURIComponent(aplicacionId)}/aplicar`;
  const response = await api.post(endpoint, {
    factura_id: facturaId,
    importe,
  });
  return response.data?.data;
}

export async function fetchCobranzas({ page = 1, pageSize = 20, search, clienteId, fechaDesde, fechaHasta, minutaId, medioPago, documentoTipo, estado, usuarioId } = {}) {
  const params = new URLSearchParams();
  params.set('page', page);
  params.set('page_size', pageSize);
  if (search) params.set('search', search);
  if (clienteId) params.set('cliente_id', clienteId);
  if (fechaDesde) params.set('fecha_desde', fechaDesde);
  if (fechaHasta) params.set('fecha_hasta', fechaHasta);
  if (minutaId) params.set('minuta_id', minutaId);
  if (medioPago) params.set('medio_pago', medioPago);
  if (documentoTipo) params.set('documento_tipo', documentoTipo);
  if (estado) params.set('estado', estado);
  if (usuarioId) params.set('usuario_id', usuarioId);
  const response = await api.get(`/api/cobranzas?${params.toString()}`);
  return response.data?.data;
}

export async function fetchMinutas(filters = {}) {
  const params = new URLSearchParams();
  const map = {
    sucursalId: 'sucursal_id', usuarioId: 'usuario_id', estado: 'estado', numero: 'numero', texto: 'texto',
    fechaDesde: 'fecha_desde', fechaHasta: 'fecha_hasta', fechaCierreDesde: 'fecha_cierre_desde', fechaCierreHasta: 'fecha_cierre_hasta',
    medioPago: 'medio_pago', tieneCobranzas: 'tiene_cobranzas', importeMinimo: 'importe_minimo', importeMaximo: 'importe_maximo',
    page: 'page', pageSize: 'page_size', sortField: 'sort_field', sortDirection: 'sort_direction',
  };
  Object.entries(map).forEach(([key, param]) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') params.set(param, value);
  });
  const response = await api.get(`/api/cobranzas/minutas?${params.toString()}`);
  return response.data?.data;
}

export async function fetchMinutasFiltros() {
  const response = await api.get('/api/cobranzas/minutas/filtros');
  return response.data?.data;
}

export async function fetchDashboardTesoreria({ periodo = 7, sucursalId } = {}) {
  const response = await api.get('/api/cobranzas/dashboard', { params: { periodo, sucursal_id: sucursalId } });
  return response.data?.data;
}

export async function fetchRecibos(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
  const response = await api.get(`/api/cobranzas/recibos${params.toString() ? `?${params.toString()}` : ''}`);
  return response.data?.data;
}

export async function fetchCheques(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
  const response = await api.get(`/api/cartera-cheques${params.toString() ? `?${params.toString()}` : ''}`);
  return response.data?.data;
}

export async function actualizarEstadoCheque(id, payload) {
  const response = await api.post(`/api/cobranzas/cheques/${id}/estado`, payload);
  return response.data?.data;
}
export async function crearTransferenciaCheques(payload) { const response = await api.post('/api/cartera-cheques/transferencias', payload); return response.data?.data; }
export async function despacharTransferenciaCheques(id) { const response = await api.post(`/api/cartera-cheques/transferencias/${id}/despachar`); return response.data?.data; }
export async function recibirTransferenciaCheques(id, detalles) { const response = await api.post(`/api/cartera-cheques/transferencias/${id}/recibir`, { detalles }); return response.data?.data; }
export async function confirmarDepositoCheques(payload) { const response = await api.post('/api/cartera-cheques/depositos/confirmar', payload); return response.data?.data; }
export async function resultadoChequeDepositado(depositoId, chequeId, estado, motivo) { const response = await api.post(`/api/cartera-cheques/depositos/${depositoId}/cheques/${chequeId}/resultado`, { estado, motivo }); return response.data?.data; }

export async function fetchCobranza(id) {
  const response = await api.get(`/api/cobranzas/${id}`);
  return response.data?.data;
}

export async function fetchRecibo(id) {
  const response = await api.get(`/api/cobranzas/recibos/${id}`);
  return response.data?.data;
}

export async function revertirAplicacionesRecibo(reciboId, motivo) {
  const response = await api.post(`/api/cobranzas/recibos/${reciboId}/aplicaciones/revertir`, { motivo });
  return response.data?.data;
}

export async function anularCobranza(cobranzaId, motivo) {
  const response = await api.post(`/api/cobranzas/${cobranzaId}/anular`, { motivo });
  return response.data?.data;
}

export async function descargarReciboPdf(id, { download = false } = {}) {
  const response = await api.get(`/api/cobranzas/recibos/${id}/pdf`, { params: download ? { download: 1 } : {}, responseType: 'blob' });
  return {
    blob: response.data,
    filename: response.headers?.['content-disposition']?.match(/filename="?([^";]+)"?/i)?.[1] || `RECIBO_${id}.pdf`,
  };
}

export async function fetchSeguimientoContrareembolsos({
  sucursalId,
  search,
  estado,
  fechaDesde,
  fechaHasta,
  clienteId,
} = {}) {
  const params = new URLSearchParams();
  if (sucursalId) params.set('sucursal_id', sucursalId);
  if (search) params.set('search', search);
  if (estado) params.set('estado', estado);
  if (fechaDesde) params.set('fecha_desde', fechaDesde);
  if (fechaHasta) params.set('fecha_hasta', fechaHasta);
  if (clienteId) params.set('cliente_id', clienteId);
  const query = params.toString();
  const response = await api.get(`/api/cobranzas/contrareembolsos/seguimiento${query ? `?${query}` : ''}`);
  return response.data?.data;
}

// CRR is a third-party value custody flow. It deliberately uses its own API
// instead of the commercial collections endpoints and receipts.
export async function fetchContrareembolsos(filters = {}) {
  const params = new URLSearchParams();
  const map = {
    sucursalId: 'sucursal_id', estado: 'estado', search: 'search', page: 'page', pageSize: 'page_size',
  };
  Object.entries(map).forEach(([key, param]) => {
    if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') params.set(param, filters[key]);
  });
  const response = await api.get(`/api/contrareembolsos${params.toString() ? `?${params.toString()}` : ''}`);
  return response.data?.data;
}

export async function exportContrareembolsosCsv(filters = {}) {
  const params = new URLSearchParams();
  const map = { sucursalId: 'sucursal_id', estado: 'estado', search: 'search', fechaDesde: 'fecha_desde', fechaHasta: 'fecha_hasta' };
  Object.entries(map).forEach(([key, param]) => {
    if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') params.set(param, filters[key]);
  });
  const response = await api.get(`/api/contrareembolsos/export.csv?${params.toString()}`, { responseType: 'blob' });
  return response.data;
}

export async function fetchContrareembolso(id) {
  const response = await api.get(`/api/contrareembolsos/${id}`);
  return response.data?.data;
}

export async function registrarValoresContrareembolso(id, payload) {
  const response = await api.post(`/api/contrareembolsos/${id}/cobros`, payload);
  return response.data?.data;
}

export async function transferirValoresContrareembolso(id, payload) {
  const response = await api.post(`/api/contrareembolsos/${id}/transferencias`, payload);
  return response.data?.data;
}

export async function recibirValoresContrareembolso(id, payload) {
  const response = await api.post(`/api/contrareembolsos/${id}/recepciones`, payload);
  return response.data?.data;
}

export async function rendirValoresContrareembolso(id, payload) {
  const response = await api.post(`/api/contrareembolsos/${id}/rendiciones`, payload);
  return response.data?.data;
}

export async function cerrarMinuta(id, payload = {}) {
  const response = await api.post(`/api/cobranzas/minuta/${id}/cerrar`, payload);
  return response.data?.data;
}

export async function anularMinuta(id, motivo) {
  const response = await api.post(`/api/cobranzas/minuta/${id}/anular`, { motivo });
  return response.data?.data;
}

export async function descargarMinutaPdf(id, { download = false } = {}) {
  const response = await api.get(`/api/cobranzas/minuta/${id}/pdf`, { params: download ? { download: 1 } : {}, responseType: 'blob' });
  return { blob: response.data, filename: response.headers?.['content-disposition']?.match(/filename="?([^";]+)"?/i)?.[1] || `minuta-${id}.pdf` };
}
